import Vue from 'vue';
import VueRouter from 'vue-router';
import store from '@/store';
import i18n from '@/i18n';

import { TENANTS, DEFAULT_TENANT } from '@/constants';
import { STATUS } from '@/api';

import ErrorNetwork from '@/views/ErrorNetwork.vue';
import ManualEntry from '@/views/ManualEntry.vue';
import Processing from '@/views/Processing.vue';
import Success from '@/views/Success.vue';

Vue.use(VueRouter);

const routes = [
  {
    // the code is optional so that the normal error handling can take care of it
    path: '/:tenant/:locale/processing/:authenticationCode?',
    name: 'Processing',
    component: Processing,
  },
  {
    path: '/:tenant/:locale/processing/:authenticationCode/network-error/',
    name: 'ErrorNetworkProcessing',
    component: ErrorNetwork,
  },
  {
    path: '/:tenant/:locale/manual/:error?',
    name: 'ManualEntry',
    component: ManualEntry,
  },
  {
    path: '/:tenant/:locale/success',
    name: 'Success',
    component: Success,
    beforeEnter: (to, from, next) => {
      const status = store.getters['streamcast/status'];
      if (status !== STATUS.READY) {
        next({
          ...to,
          ...{
            name: 'Processing',
          },
        });
        return false;
      }
      next();
      return true;
    },
  },
  {
    path: '/:tenant/:locale/network-error/',
    name: 'ErrorNetworkBlank',
    component: ErrorNetwork,
  },
  {
    path: '/:tenant?/:locale?/*',
    alias: ['*'],
    redirect: (to) => {
      // if there aren't even the network parameter present
      // go directly to the "ErrorNetwork" page
      if (
        !to.query?.client_ip
        || !to.query?.client_mac
        || !to.query?.site_id
        || !to.query?.signature
      ) {
        return {
          ...to,
          ...{
            name: 'ErrorNetworkBlank',
          },
        };
      }

      // otherwise, users should be prompted to enter the code manually
      return {
        ...to,
        ...{
          name: 'ManualEntry',
          params: {
            ...to.params,
          },
        },
      };
    },
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

// keep query params between to pages
const hasQueryParams = (route) => !!Object.keys(route.query).length;
router.beforeEach((to, from, next) => {
  if (!hasQueryParams(to) && hasQueryParams(from)) {
    next({
      ...to,
      ...{ query: from.query },
    });
  } else {
    next();
  }
});

// tenant and localization based on routing
router.beforeEach((to, from, next) => {
  let change = false;

  // 1) tenant validation
  let targetTenant = to.params?.tenant;
  if (!TENANTS.includes(targetTenant)) {
    targetTenant = DEFAULT_TENANT;
    change = true;
  }

  // 2) localization validation
  const supportedLocales = i18n.availableLocales || [];

  // for the moment, keep the current locale
  let targetLocale = to.params?.locale || from.params.locale;

  // if there is no locale set, or the locale currently present is not supported...
  if (!targetLocale || !supportedLocales.includes(targetLocale)) {
    // detect the user locale
    const userLocale = navigator.language || navigator.userLanguage;
    const localePattern = new RegExp(`^${userLocale}\\b`);
    const localeIndex = supportedLocales.findIndex((item) => localePattern.test(item));

    // use the user locale if supported, else use the fallback / default locale
    targetLocale = (localeIndex > -1) ? supportedLocales[localeIndex] : i18n.fallbackLocale;
  }

  // set the locale
  if (i18n.locale !== targetLocale) {
    i18n.locale = targetLocale;
  }

  if (to.params.locale !== targetLocale) {
    change = true;
  }

  // 3) apply params if there is a change
  if (change) {
    const target = to;
    target.params.tenant = targetTenant;
    target.params.locale = targetLocale;
    next({ ...target });
  } else {
    next();
  }
});

/**
 * $toLocale offers a way to easily navigate to another view while changing or keeping the locale.
 *
 * @param {String|undefined} locale The locale to switch to.
 * If not set (undefined) or not supported, the current locale is used.
 *
 * @param {Route|undefined} to Route definition on which the locale needs to be changed.
 * If not set (undefined), the function is using the current route instead.
 *
 * @returns Route definition
 */
router.$toLocale = ({ locale, to } = { }) => {
  let route = to;
  if (!route) {
    route = router.currentRoute;
  }

  const supportedLocales = i18n.availableLocales || [];
  let targetLocale = locale;
  if (!targetLocale || !supportedLocales.includes(targetLocale)) {
    targetLocale = router.currentRoute.params?.locale || i18n.fallbackLocale;
  }

  return {
    ...route,
    ...{
      params: {
        ...route.params,
        ...{
          locale: targetLocale,
        },
      },
    },
  };
};

export default router;
